// The one line a signer reads before approving a Control Plane act.
//
// A proposal row already states its class, its threshold and its status. None
// of that says what it is FOR, and a signer asked to approve
// `LOW CONFIG · proposed · 0 of 1` has been asked to approve a class.
//
// Both approvals screens key their summary on a `payload.action` string. A
// Control Plane payload has no `action` — it has `operation` — so both
// rendered nothing at all for the family whose entire point is that the
// approval is bound to a specific product, lane and target.
//
// ─── Why this is a separate function and not a merge of the two ───────────
//
// The two screens' summarisers have already drifted: the list understands
// `escrow_refund`, the history does not. Merging them would hand the history
// screen a branch it does not have today — a behaviour change to an unrelated
// surface, smuggled in under a Control Plane change. So each screen keeps its
// own body untouched and both consult this first. The new family is added
// once; nothing else moves.
//
// ─── Display only ─────────────────────────────────────────────────────────
//
// Not a security decision. The server redacts banned fields before sending the
// row and the executor re-derives every committed field itself. It still has
// to be RIGHT — a signer reads it before approving — so every branch renders
// only fields it has type-checked, and anything else returns null rather than
// a guess.

function str(o: Record<string, unknown>, k: string): string | null {
  const v = o[k];
  return typeof v === "string" && v !== "" ? v : null;
}

/** `null` when this is not a Control Plane payload, so a caller can fall
 *  through to its own branches unchanged. */
export function summariseControlPlanePayload(p: unknown): string | null {
  if (p === null || typeof p !== "object") return null;
  const o = p as Record<string, unknown>;
  if (o["domain"] !== "product.control_plane") return null;

  const op = str(o, "operation") ?? "?";
  const product = str(o, "productId") ?? "?";
  const lane = str(o, "lane") ?? "?";
  const target = str(o, "target");
  // The lane belongs in the summary above almost everything else: an approval
  // on one lane is never spendable on another, and a signer who cannot see
  // PRODUCT in the row cannot apply the care that fact deserves.
  const laneLabel = lane === "production" ? "PRODUCT" : lane.toUpperCase();
  const what =
    target === null
      ? op === "bootstrap"
        ? "genesis — unbound (no content digest; refused at execution)"
        : "no target"
      : op === "rollback"
        ? target
        : target.length > 20
          ? `${target.slice(0, 8)}\u2026${target.slice(-8)}`
          : target;
  return `control plane ${op} \u00b7 ${product} \u00b7 ${laneLabel} \u00b7 ${what}`;
}
